// src/services/matrixAuthService.js
// Matrix authentication service - handles login and credential management

import { createClient } from 'matrix-js-sdk';
import logger from '../utils/logger';
import { RECOVERY_SKIPPED_KEY } from '../context/RecoveryContext/storage';
import { cachePasswordCredential, clearCredentials } from './authCredentialService';
import { DEFAULT_HOMESERVER } from '../config/homeserver';
import { timFetch } from '../utils/timUserAgent';
import { resetInactiveDeletionConfig } from './serverConfigService';
// Namespace import: this module also imports a *different* clearCredentials
// from authCredentialService (above), so credentialStore must be namespaced.
import * as credentialStore from './credentialStore';
import { configureSdkLogging } from './sdkLogConfig';

// Quiet the matrix-js-sdk console logging before the login flow creates its first
// (temporary) client, regardless of which service module loads first (NED-4003).
// Idempotent with the identical call in matrixService.js.
configureSdkLogging();

// Module-scoped in-flight promise for recovery detection
let recoveryDetectionInFlight = null;

/**
 * Check if an identifier looks like an email address
 * Note: This is intentionally permissive - the server will validate
 * We just need to distinguish email from Matrix usernames
 * @param {string} identifier - The login identifier
 * @returns {boolean} - True if it looks like an email
 */
function isEmailAddress(identifier) {
  if (!identifier || typeof identifier !== 'string') {
    return false;
  }
  // Email pattern: contains @ and at least one . after the @
  // Excludes Matrix IDs like @user:server.com (starts with @)
  // Excludes plain usernames (no @ at all)
  return !identifier.startsWith('@') && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier);
}

// Trusted domains for Matrix server validation (must stay in sync with anchorService.js)
const TRUSTED_MATRIX_DOMAINS = ['netsfere.com', 'netsferedev.com', 'netsferetest.com'];

/**
 * Parse a fully-qualified Matrix ID (e.g. @user:server.example.com)
 * and extract the server name if it belongs to a trusted domain.
 * @param {string} identifier - The login identifier
 * @returns {{ localpart: string, serverName: string } | null}
 *   Returns parsed result, or null if not a valid trusted Matrix ID
 */
function parseMatrixId(identifier) {
  if (!identifier || typeof identifier !== 'string' || !identifier.startsWith('@')) {
    return null;
  }
  const colonIdx = identifier.indexOf(':');
  if (colonIdx < 2) return null; // need at least @x:
  const serverName = identifier.slice(colonIdx + 1);
  if (!serverName || !serverName.includes('.')) return null;
  // Validate against trusted domains
  const lower = serverName.toLowerCase();
  const trusted = TRUSTED_MATRIX_DOMAINS.some(d => lower === d || lower.endsWith('.' + d));
  if (!trusted) return null;
  return { localpart: identifier.slice(1, colonIdx), serverName };
}

/**
 * Matrix Authentication Service
 * Handles Matrix login, credential storage, and session management
 */
class MatrixAuthService {
  constructor() {
    this.defaultHomeserver = DEFAULT_HOMESERVER;
    this.defaultIdentityServer = 'https://vector.im';
    this.recoveryActions = null; // Will be set by app initialization
    this.tokenRefreshPromise = null; // deduplicate concurrent refresh attempts
    this.currentRefreshToken = null; // track which token is being refreshed
    this._sessionInvalidating = false; // single-flight guard for refresh-401 teardown
    // Monotonic session counter. Bumped on every successful login so an
    // in-flight recovery detection promise from a prior session can't be
    // coalesced with a new session's detection request.
    this._loginNonce = 0;
  }

  /**
   * NED-3466 follow-up: reset the cross-session dedup state that a new login
   * must not inherit. Called from logout() so the next session's detection
   * starts fresh and no stale refresh-token promise is coalesced against.
   *
   * WHAT GETS RESET — and why these three:
   *   - recoveryDetectionInFlight: module-scoped promise used for coalescing
   *     concurrent detectRecoveryRequirement() callers. No teardown owner.
   *   - tokenRefreshPromise / currentRefreshToken: instance state used for
   *     refreshToken() deduplication. Self-clears via the .finally inside
   *     refreshToken(), but clearing here defends against the case where a
   *     prior session ended before the in-flight refresh resolved.
   *
   * WHAT IS INTENTIONALLY NOT RESET:
   *   - _sessionInvalidating: owned by the refresh-401 branch's try/finally.
   *     Clearing it mid-teardown would defeat the single-flight guard and
   *     allow a second concurrent 401 (arriving with a different refresh
   *     token) to re-enter the pipeline. The nonce-bump sites in login()
   *     and finalizeLogin() reset it defensively on successful re-login.
   * @private
   */
  _resetGuards() {
    recoveryDetectionInFlight = null;
    this.tokenRefreshPromise = null;
    this.currentRefreshToken = null;
  }

  async refreshToken(refreshToken, homeserverUrl = null, options = {}) {
    // options.suppressInvalidationSideEffects forwards to _performTokenRefresh
    // (see there). NOTE on single-flight: if a suppressed (gate) refresh and a
    // normal (runtime) refresh of the SAME token overlapped, the in-flight one's
    // options would win. They don't overlap in practice — the gate refreshes
    // BEFORE the MatrixClient is initialized, and the runtime tokenRefreshFunction
    // only runs after, against a fresh token — so the cached-promise reuse is safe.
    // Only deduplicate if refreshing the SAME token
    if (this.tokenRefreshPromise && this.currentRefreshToken === refreshToken) {
      logger.debug('refreshToken: Same token refresh in progress, waiting...');
      return this.tokenRefreshPromise;
    }

    // If different token provided while refresh in progress, wait for current to finish
    // Use loop to handle case where another refresh starts while we're waiting
    while (this.tokenRefreshPromise) {
      logger.debug('refreshToken: Different token provided, waiting for current refresh to complete...');
      await this.tokenRefreshPromise;
    }

    // Store current refresh token for comparison
    this.currentRefreshToken = refreshToken;

    // Create and store the Promise
    this.tokenRefreshPromise = this._performTokenRefresh(refreshToken, homeserverUrl, options)
      .finally(() => {
        // Clear the promise and token reference when done (success or failure)
        this.tokenRefreshPromise = null;
        this.currentRefreshToken = null;
      });

    logger.debug('refreshToken: Returning refreshed token');
    return this.tokenRefreshPromise;
  }

  async _performTokenRefresh(refreshToken, homeserverUrl = null, options = {}) {
    // suppressInvalidationSideEffects: when the CALLER owns teardown (the startup
    // restore gate runs its own logout + shows the login form), skip the
    // mid-session invalidation side effects on a 401 — the internal logout, the
    // 'matrix:session-invalidated' sessionStorage flag, and the event dispatch.
    // Those exist to surface a "session expired" modal when a LIVE session dies;
    // firing them at cold-start would pop that modal on a routine relaunch with
    // an aged-out token. The classified 401 error is still thrown so the gate
    // can distinguish a dead refresh token from a transient failure.
    const { suppressInvalidationSideEffects = false } = options;
    if (!refreshToken || typeof refreshToken !== 'string') {
      logger.error('_performTokenRefresh: Invalid refresh token provided');
      throw new Error('Invalid refresh token: must be a non-empty string');
    }

    try {
      logger.debug('_performTokenRefresh: Refreshing Matrix access token...');
      const creds = this.getStoredCredentials();

      // Use homeserverUrl from credentials if available, otherwise use passed parameter
      const effectiveHomeserverUrl = creds?.homeserverUrl || homeserverUrl;

      if (!effectiveHomeserverUrl) {
        throw new Error('_performTokenRefresh: No homeserver URL available for token refresh');
      }

      // Note: Matrix /refresh endpoint doesn't require Authorization header
      // The refresh_token in the body is sufficient for authentication
      const response = await timFetch(`${effectiveHomeserverUrl}/_matrix/client/v3/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        const error = await response.json();
        logger.error('Token refresh failed:', error);

        const refreshError = new Error(error.error || 'Token refresh failed');
        refreshError.errcode = error.errcode;
        refreshError.httpStatus = response.status;

        // If refresh returns 401, session is invalid - force logout
        // This handles: M_UNKNOWN_TOKEN, server restart, or any 401 from /refresh
        if (response.status === 401) {
          logger.warn('Refresh token rejected (401):', error.errcode || 'no errcode');

          // Caller-owned teardown (startup restore gate): throw the classified
          // 401 without running the mid-session invalidation pipeline. The gate
          // tears down via its own logout({skipServerLogout:true}) and shows the
          // login form — no "session expired" modal, no double-logout.
          if (suppressInvalidationSideEffects) {
            throw refreshError;
          }

          // Single-flight: concurrent 401s must not double-enter teardown.
          if (this._sessionInvalidating) {
            throw refreshError;
          }
          this._sessionInvalidating = true;

          try {
            // NED-3466 follow-up: run the full clean-slate logout pipeline
            // (matrixService.cleanup, IndexedDB deletion, storage purge,
            // module-guard reset) before dispatching the event. Previously
            // this path fired a detached IIFE that only called cleanup(),
            // leaving stale module-scoped detection promises behind and
            // stranding the next session on a 30 s timeout → forced logout.
            //
            // skipServerLogout: the homeserver just rejected this token, so
            // calling /logout with it is wasted work and needlessly surfaces
            // the dead token in server logs.
            try {
              await this.logout({ skipServerLogout: true });
            } catch (logoutErr) {
              const msg = logoutErr?.message || '';
              if (msg.includes('null pointer') ||
                  msg.includes('OutgoingRequestsManager') ||
                  msg.includes('was stopped')) {
                logger.debug('Non-fatal crypto shutdown race during refresh-401 invalidation:', msg);
              } else {
                logger.error('refresh-401: logout failed (continuing):', logoutErr);
              }
            }

            // logout() clears this flag as part of sessionStorage cleanup;
            // re-assert it here so LoginPage's mount-time reader still finds
            // it after the dispatch-driven remount.
            try {
              sessionStorage.setItem('matrix:session-invalidated', JSON.stringify({
                reason: error.errcode || 'Refresh token rejected',
                timestamp: Date.now(),
              }));
            } catch (e) {
              logger.debug('Could not set session invalidation flag:', e);
            }
          } finally {
            // Notify RecoveryProvider explicitly before the UI remount so it
            // can't pick up stale reducer state (see Step 3 rationale).
            try { this.recoveryActions?.reset?.(); } catch { /* non-fatal */ }

            window.dispatchEvent(new CustomEvent('matrix:session-invalidated', {
              detail: { reason: error.errcode || 'Refresh token rejected' },
            }));
            this._sessionInvalidating = false;
          }
        }

        throw refreshError;
      }
      
      const data = await response.json();
      // Validate response structure - include field presence info for debugging
      if (!data.access_token || !data.refresh_token) {
        const presentFields = Object.keys(data).filter(k => !k.includes('token')).join(', ');
        const hasAccessToken = !!data.access_token;
        const hasRefreshToken = !!data.refresh_token;
        logger.error('Invalid token refresh response structure:', {
          hasAccessToken,
          hasRefreshToken,
          presentFields: presentFields || 'none',
          responseKeys: Object.keys(data).length,
        });
        throw new Error(`Invalid server response: missing required token fields (access_token: ${hasAccessToken}, refresh_token: ${hasRefreshToken})`);
      }

      logger.debug('Token refreshed successfully');

      // Update stored credentials with new tokens
      if (creds) {
        const updatedCreds = {
          ...creds,
          accessToken: data.access_token,
          refreshToken: data.refresh_token,
          expiresInMs: data.expires_in_ms,
        };
        this.storeCredentials(updatedCreds);
      } else {
        // Credentials were cleared from localStorage but refresh succeeded
        // Re-create minimal credentials to ensure session persists
        // Without this, the next token refresh would fail with "No homeserver URL available"
        logger.warn('Token refresh succeeded but localStorage credentials missing - recreating minimal credentials');
        const minimalCreds = {
          homeserverUrl: effectiveHomeserverUrl,
          accessToken: data.access_token,
          refreshToken: data.refresh_token,
          expiresInMs: data.expires_in_ms,
          // userId is not available here, but the client should have it
          // The session will work until the user needs userId-specific operations
        };
        this.storeCredentials(minimalCreds);
        logger.info('Minimal credentials stored to preserve session persistence');
      }

      return {
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiry: data.expires_in_ms ? new Date(Date.now() + data.expires_in_ms) : undefined,
      };  
    } catch (error) {
      logger.error('Error during token refresh:', error);
      throw error;
    }
  }
  /**
   * Authenticate with Matrix homeserver
   */
  async login(credentials, options = {}) {
    const {
      homeserver = this.defaultHomeserver,
      identityServer = this.defaultIdentityServer,
      loginType = 'm.login.password',
      skipRecoveryCheck = false, // Option to skip recovery detection
    } = options;

    try {
      logger.debug('Attempting Matrix login to:', homeserver);

      // Create temporary client for login
      const tempClient = createClient({
        baseUrl: homeserver,
        idBaseUrl: identityServer,
        fetchFn: timFetch,
      });

      // Prepare login parameters
      // Determine the identifier type based on what the user entered
      const userIdentifier = credentials.email || credentials.username;
      let identifier;

      if (isEmailAddress(userIdentifier)) {
        // Email login: use m.id.thirdparty with medium 'email'
        // Server will resolve the email to the actual Matrix user ID
        logger.debug('Login: Using email identifier type for:', userIdentifier);
        identifier = {
          type: 'm.id.thirdparty',
          medium: 'email',
          address: userIdentifier,
        };
      } else {
        // Username login: use m.id.user (standard Matrix username)
        logger.debug('Login: Using user identifier type for:', userIdentifier);
        identifier = {
          type: 'm.id.user',
          user: userIdentifier,
        };
      }

      const loginParams = {
        password: credentials.password,
        identifier,
        refresh_token: true,
      };

      // Perform login
      const loginResponse = await tempClient.login(loginType, loginParams);
      logger.debug('Matrix login successful:', {
        userId: loginResponse.user_id,
        deviceId: loginResponse.device_id,
      });

      // Cache password for UIA (10-minute expiry, like reference client)
      // This enables initial bootstrap to complete without prompting for password
      if (loginType === 'm.login.password' && credentials.password) {
        cachePasswordCredential(credentials.password, loginResponse.user_id);
      }

      // Create credentials object
      const matrixCredentials = {
        homeserverUrl: homeserver,
        identityServerUrl: identityServer,
        userId: loginResponse.user_id,
        deviceId: loginResponse.device_id,
        accessToken: loginResponse.access_token,
        wellKnown: loginResponse.well_known,
        refreshToken : loginResponse.refresh_token,
        expiresInMs: loginResponse.expires_in_ms,
      };

      // If recovery check is not skipped, finalize with recovery detection
      if (!skipRecoveryCheck) {
        const finalizeResult = await this.finalizeLogin(matrixCredentials);
        return {
          success: true,
          credentials: matrixCredentials,
          user: this.extractUserInfo(matrixCredentials),
          client: finalizeResult.client,
        };
      } else {
        // Just store credentials without recovery check
        this.storeCredentials(matrixCredentials);
        // Bump session nonce — same reason as in finalizeLogin().
        this._loginNonce++;
        // Defensive: if a prior session ended mid-teardown (exception in the
        // 401 branch before the finally), this flag could be stuck true.
        // A fresh login is the correct point to clear it.
        this._sessionInvalidating = false;
        return {
          success: true,
          credentials: matrixCredentials,
          user: this.extractUserInfo(matrixCredentials),
        };
      }

    } catch (error) {
      logger.error('Matrix login failed:', error);

      // Return structured error
      return {
        success: false,
        error: {
          code: error.errcode || 'UNKNOWN_ERROR',
          message: error.message || 'Login failed',
          details: error,
        },
      };
    }
  }

  /**
   * Store Matrix credentials securely
   * Delegates to the single credential-store owner (F-04).
   */
  storeCredentials(credentials) {
    credentialStore.setCredentials(credentials);
  }

  /**
   * Get stored Matrix credentials (validated).
   * Delegates to the credential-store owner, which validates required fields
   * and lightweight-clears an invalid/unparseable blob (F-04).
   */
  getStoredCredentials() {
    return credentialStore.getCredentials();
  }

  /**
   * Clear stored credentials
   */
  clearCredentials() {
    // Capture userId BEFORE removing matrixCreds so we can namespace-clear
    // this user's persistent unread floors (NED-3439 plan A.4). Other
    // accounts' floors are preserved (shared-browser case).
    let priorUserId = null;
    try {
      const creds = credentialStore.readCredentials();
      if (creds && typeof creds.userId === 'string') {
        priorUserId = creds.userId;
      }
    } catch {
      // ignore — parse errors just mean we skip the namespaced clear
    }

    // Lightweight key-remove via the owner; the namespaced side effects below
    // (muted-cache / unread-floor clears) remain this method's responsibility.
    credentialStore.clearCredentials();

    // Clear the per-browser dev toggle for the mock-authenticator path.
    // Session-scoped: each new login starts with the real authenticator flow.
    try {
      const { setMockAuthenticatorEnabled } = require('../utils/devToggles');
      setMockAuthenticatorEnabled(false);
    } catch {
      // Non-critical — fall back to LoginContext.logout()'s catch-all wipe.
    }

    // Clear muted-room unread count cache to prevent cross-session data leakage.
    // Placed here (rather than only in logout()) so all session-teardown paths
    // (401 rejection, session invalidation, etc.) also clear the cache.
    try {
      const { clearAllMutedUnreadCaches } = require('../utils/unreadCountUtils');
      clearAllMutedUnreadCaches();
    } catch {
      // Non-critical — module may not be loaded yet during early init
    }

    // Clear unread-backfill per-room state (NED-3439). Same lifetime as the
    // muted-unread cache: both are session-scoped and must be wiped on logout
    // to prevent cross-session leakage.
    try {
      const { clearAllUnreadBackfillState } = require('../utils/unreadBackfill');
      clearAllUnreadBackfillState();
    } catch {
      // Non-critical — module may not be loaded yet during early init
    }

    // Clear the persistent unread-floor cache scoped to this user.
    if (priorUserId) {
      try {
        const { clearFloorsForUser } = require('../utils/unreadFloor');
        clearFloorsForUser(priorUserId);
      } catch {
        // Non-critical — module may not be loaded yet during early init
      }
    }
  }

  /**
   * Check if user has valid stored credentials
   */
  hasValidCredentials() {
    const credentials = this.getStoredCredentials();
    return credentials !== null;
  }

  /**
   * Validate credentials by making a test request
   */
  async validateCredentials(credentials = null) {
    const creds = credentials || this.getStoredCredentials();
    if (!creds) {
      return { valid: false, error: 'No credentials found' };
    }

    try {
      // Create client with credentials
      const testClient = createClient({
        baseUrl: creds.homeserverUrl,
        accessToken: creds.accessToken,
        userId: creds.userId,
        deviceId: creds.deviceId,
        fetchFn: timFetch,
      });

      // Test with a simple whoami request
      const whoami = await testClient.whoami();
      
      if (whoami.user_id === creds.userId) {
        return { valid: true };
      } else {
        return { valid: false, error: 'User ID mismatch' };
      }
      
    } catch (error) {
      logger.error('Credential validation failed:', error);
      
      // If credentials are invalid, clear them
      if (error.errcode === 'M_UNKNOWN_TOKEN' || error.errcode === 'M_MISSING_TOKEN') {
        this.clearCredentials();
      }
      
      return {
        valid: false,
        error: error.message || 'Credential validation failed',
        code: error.errcode,
        // Surface the HTTP status so callers can distinguish a DEFINITIVE token
        // rejection (401 — invalid/revoked token, e.g. corrupted macaroon or a
        // server-side logout/all that deleted the device) from a transient
        // transport failure (no httpStatus). Restore-time validation only
        // tears down the session on a definitive rejection.
        httpStatus: error.httpStatus,
      };
    }
  }

  /**
   * Extract user information from credentials
   */
  extractUserInfo(credentials) {
    const userId = credentials.userId;
    
    // Parse Matrix user ID (@username:homeserver.com)
    const userIdMatch = userId.match(/^@([^:]+):(.+)$/);
    const username = userIdMatch ? userIdMatch[1] : userId;
    const homeserver = userIdMatch ? userIdMatch[2] : credentials.homeserverUrl;

    return {
      id: userId,
      username: username,
      displayName: username, // Will be updated from Matrix profile later
      homeserver: homeserver,
      email: null, // Not available from Matrix credentials
      avatar: null, // Will be loaded from Matrix profile later
    };
  }

  /**
   * Logout from Matrix
   * @param {Object} [options]
   * @param {boolean} [options.skipServerLogout] - When true, skip the
   *   server-side /logout call. Used from the refresh-401 path where the
   *   access/refresh tokens have already been rejected by the homeserver.
   */
  async logout(options = {}) {
    const { skipServerLogout = false } = options;
    logger.info(`LOGOUT: Starting logout process${skipServerLogout ? ' (skipServerLogout)' : ''}`);

    // Track if any IndexedDB deletion fails (will trigger page reload in LoginContext)
    let indexedDBDeletionFailed = false;

    // Clear session invalidation flag (prevents stale state on next login)
    try {
      sessionStorage.removeItem('matrix:session-invalidated');
    } catch (e) {
      // Ignore - non-critical
    }

    // Clear cached credentials for UIA immediately
    // (also clears muted-room unread cache via clearCredentials)
    clearCredentials();

    // Wipe the module-scoped server-config cache so the next user's session
    // refetches against their homeserver. Defends against cross-user bleed
    // when logout completes without a page reload.
    try { resetInactiveDeletionConfig(); } catch { /* non-fatal */ }

    // NED-3466 follow-up: drop the module-scoped recovery-detection promise
    // and the refresh/invalidating guards BEFORE anything async runs, so a
    // concurrent re-login can't coalesce its detection with the outgoing
    // session's in-flight promise.
    this._resetGuards();

    const credentials = this.getStoredCredentials();
    logger.debug('LOGOUT: Current user:', credentials ? `${credentials.userId}:${credentials.deviceId}` : 'none');

    // 1. Stop Matrix client and perform server logout
    if (credentials) {
      try {
        logger.debug('LOGOUT: Importing MatrixService');
        // Import MatrixService to stop client properly
        const { default: matrixService } = await import('./matrixService');

        // Cleanup Matrix service (stops sync, removes listeners, clears client)
        if (matrixService.client) {
          logger.debug('LOGOUT: Cleaning up Matrix service');
          try {
            await matrixService.cleanup();
          } catch (cleanupErr) {
            // Demote the known crypto-shutdown race errors to debug; anything
            // else is surfaced but does not abort the remainder of logout().
            const msg = cleanupErr?.message || '';
            if (msg.includes('null pointer') ||
                msg.includes('OutgoingRequestsManager') ||
                msg.includes('was stopped')) {
              // logout() runs from many flows (user sign-out, refresh-401,
              // sync-error session-invalidated). Keep the message generic.
              logger.debug('Non-fatal crypto shutdown race during logout:', msg);
            } else {
              logger.error('LOGOUT: matrixService.cleanup() failed (continuing):', cleanupErr);
            }
          }

          // Wait for crypto resources to be fully released before creating temp client
          // This prevents race conditions with crypto operations during logout
          logger.debug('LOGOUT: Waiting for crypto cleanup to complete...');
          await new Promise(resolve => setTimeout(resolve, 500));
        }

        if (skipServerLogout) {
          logger.debug('LOGOUT: Skipping server /logout (token already rejected)');
        } else {
          logger.debug('LOGOUT: Creating temporary client for server logout');
          // Create temporary client for logout
          // Wrap in try-catch to handle potential crypto race conditions gracefully
          try {
            const client = createClient({
              baseUrl: credentials.homeserverUrl,
              accessToken: credentials.accessToken,
              userId: credentials.userId,
              deviceId: credentials.deviceId,
              fetchFn: timFetch,
            });

            // Logout from Matrix server
            await client.logout();
            logger.debug('LOGOUT: Matrix server logout successful');
          } catch (logoutError) {
            // Check if this is an expected crypto race condition error
            const errorMsg = logoutError.message || '';
            if (errorMsg.includes('null pointer') ||
                errorMsg.includes('OutgoingRequestsManager') ||
                errorMsg.includes('was stopped')) {
              logger.debug('LOGOUT: Expected crypto race condition during shutdown, continuing with cleanup...');
            } else {
              // Unexpected error - log it but don't throw
              logger.error('LOGOUT: Unexpected server logout error:', logoutError);
            }
            // Continue with local cleanup regardless of server logout result
          }
        }

      } catch (error) {
        logger.error('LOGOUT: Matrix server logout failed:', error);
        // Continue with local cleanup even if server logout fails
      }
    } else {
      logger.debug('LOGOUT: No credentials found, skipping server logout');
    }

    // 2. Clear all localStorage items
    try {
      logger.debug('LOGOUT: Clearing Matrix credentials from localStorage');
      // Clear Matrix credentials
      this.clearCredentials();

      // Clear all Matrix-related localStorage items (but preserve user preferences).
      // NOTE: 'matrixCreds' is intentionally NOT listed here — it is owned by the
      // credential-store and already removed by this.clearCredentials() above
      // (F-04: all matrixCreds access goes through the single owner).
      const keysToRemove = [
        'matrixForceClearStores',
        'matrixForceDeviceReset',
        'matrixResetDeviceKeys',
        'matrixDisableCrypto',
        'matrixKeyConflictDetected',
        'matrixTempPassword',
        'TI-M_FEATURE_FLAGS',
        'ti_app_settings', // Clear settings - now stored in Matrix account data per-user
        'ti_last_activity_timestamp', // Clear auto-logout activity timestamp
        'recovery:completed', // Clear recovery completion marker
        'ssoLoginEmail', // Clear SSO login email used for UIA login_hint
        'tim:downloaded-attachments', // Clear per-user malware-toast suppression set
      ];

      keysToRemove.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch (e) {
          logger.debug(`Could not remove localStorage key ${key}:`, e);
        }
      });

      // Clear session marker for THIS device only (not other tabs/devices)
      // Note: credentials.userId and credentials.deviceId are available from getStoredCredentials() called earlier
      if (credentials && credentials.userId && credentials.deviceId) {
        const sessionMarkerKey = `matrix_session_${credentials.userId}_${credentials.deviceId}`;
        try {
          localStorage.removeItem(sessionMarkerKey);
          logger.debug(`LOGOUT: Removed session marker: ${sessionMarkerKey}`);
        } catch (e) {
          logger.debug(`Could not remove session marker ${sessionMarkerKey}:`, e);
        }
      }

      logger.debug('LOGOUT: localStorage cleared');
    } catch (error) {
      logger.error('LOGOUT: Failed to clear localStorage:', error);
    }

    // 3. Clear all sessionStorage items (especially recovery state)
    try {
      logger.debug('LOGOUT: Starting sessionStorage cleanup');

      // Clear recovery-related sessionStorage more thoroughly
      const sessionKeysToRemove = [
        'recovery:v1', // Main recovery state key
        'recovery:v0', // Old recovery state key
        'recovery/ek', // Ephemeral key storage
        'recovery_state',
        'recovery_modal_state',
        'matrix_recovery',
      ];

      // Clear all keys that match patterns
      const allSessionKeys = Object.keys(sessionStorage);
      logger.debug(`LOGOUT: Found ${allSessionKeys.length} keys in sessionStorage`);

      allSessionKeys.forEach(key => {
        // Remove any recovery-related keys (including ti_m_recovery_key_* for export feature)
        if (key.startsWith('recovery') ||
            key.includes('recovery') ||
            key.startsWith('ti_m_recovery_key_') ||
            sessionKeysToRemove.includes(key)) {
          try {
            const value = sessionStorage.getItem(key);
            logger.debug(`LOGOUT: Removing sessionStorage key: ${key}, had value: ${value?.substring(0, 50)}...`);
            sessionStorage.removeItem(key);
            logger.debug(`LOGOUT: Successfully removed sessionStorage key: ${key}`);
          } catch (e) {
            logger.debug(`LOGOUT: Could not remove sessionStorage key ${key}:`, e);
          }
        }
      });

      // Verify recovery state is cleared
      const checkRecoveryState = sessionStorage.getItem('recovery:v1');
      if (checkRecoveryState) {
        logger.warn('LOGOUT: recovery:v1 still exists after cleanup!', checkRecoveryState);
      } else {
        logger.debug('LOGOUT: recovery:v1 successfully removed from sessionStorage');
      }

      logger.debug('LOGOUT: sessionStorage cleared');
    } catch (error) {
      logger.error('LOGOUT: Failed to clear sessionStorage:', error);
    }

    // 4. Clear recovery keys and state
    try {
      logger.debug('LOGOUT: Starting recovery state cleanup');

      // Get session ID before clearing credentials
      const sessionId = credentials ? `${credentials.userId}:${credentials.deviceId}` : null;
      logger.debug('LOGOUT: Session ID for recovery cleanup:', sessionId);

      // Import recovery services dynamically to avoid circular deps
      const { default: recoveryKeyValidator } = await import('./RecoveryKeyValidator');
      const { default: recoveryEphemeralStore } = await import('./recoveryEphemeralStore');
      const { clearState: clearRecoveryState } = await import('../context/RecoveryContext/storage');

      // Clear recovery keys for this session
      if (sessionId) {
        logger.debug('LOGOUT: Clearing recovery keys for session');
        recoveryKeyValidator.clear(sessionId);
      }

      // Clear ephemeral store
      logger.debug('LOGOUT: Clearing ephemeral recovery store');
      recoveryEphemeralStore.clear();

      // Clear recovery context state from sessionStorage
      logger.debug('LOGOUT: Calling clearRecoveryState()');
      clearRecoveryState();

      // Double-check recovery state was cleared
      const finalCheck = sessionStorage.getItem('recovery:v1');
      if (finalCheck) {
        logger.error('LOGOUT: recovery:v1 STILL exists after all cleanup attempts!', finalCheck);
        // Force remove it
        sessionStorage.removeItem('recovery:v1');
        logger.debug('LOGOUT: Force removed recovery:v1');
      }

      logger.debug('LOGOUT: Recovery keys and state cleared on logout');
    } catch (error) {
      logger.error('Failed to clear recovery keys on logout:', error);
      // Non-fatal - continue with logout
    }

    // 5. Clear avatar cache (blob URLs and IndexedDB)
    // Note: IndexedDB avatar cache will also be deleted when deleteDatabase() is called,
    // but we clear it here explicitly to revoke blob URLs and prevent memory leaks
    try {
      logger.debug('LOGOUT: Clearing avatar cache and revoking blob URLs');
      
      // Import and call avatar manager cleanup (stops GC timer + clears cache)
      const { avatarManager } = await import('../utils/avatarManager');
      avatarManager.cleanup();
      
      logger.debug('LOGOUT: Avatar cache and blob URLs cleared successfully');
    } catch (error) {
      logger.error('LOGOUT: Failed to clear avatar cache:', error);
      // Non-fatal - continue with logout
    }

    // 6. Clear ALL Matrix/media-related IndexedDB databases
    // We must delete everything because the Rust crypto SDK uses a shared database
    // that doesn't include user identification, causing conflicts between users
    try {
      logger.debug('LOGOUT: Starting IndexedDB cleanup');
      // Note: Crypto connections are already closed by matrixService.cleanup() called earlier
      // which internally uses stopAndWaitForCryptoShutdown()

      // F-06: explicitly close + delete the app-owned media cache FIRST. The
      // MediaCacheService singleton holds an open handle (its constructor opens
      // the DB), which would otherwise BLOCK the enumeration delete below and
      // leave cached media blobs behind on a shared device after logout.
      // Closing its handle makes the delete succeed synchronously — no reload
      // needed. If it is still blocked (e.g. a second tab), we flag the failure
      // so the existing reload + next-load wipe fallback kicks in.
      try {
        const { default: mediaCacheService } = await import('./mediaCacheService');
        const mediaResult = await mediaCacheService.deleteDatabase();
        if (mediaResult && mediaResult.blocked) {
          indexedDBDeletionFailed = true;
          logger.warn('LOGOUT: media cache deletion blocked — will rely on next-load wipe');
        }
      } catch (mediaErr) {
        logger.debug('LOGOUT: media cache cleanup failed (continuing):', mediaErr);
      }

      // Helper function to delete a database with timeout
      const deleteDBWithTimeout = (dbName, timeoutMs = 5000) => {
        return new Promise((resolve) => {
          // Setup timeout
          const timeoutId = setTimeout(() => {
            logger.warn(`LOGOUT: Database deletion timed out for: ${dbName}`);
            resolve(false); // Resolve with failure but don't throw
          }, timeoutMs);

          try {
            const deleteReq = window.indexedDB.deleteDatabase(dbName);

            deleteReq.onsuccess = () => {
              clearTimeout(timeoutId);
              logger.debug(`LOGOUT: Successfully deleted database: ${dbName}`);
              resolve(true);
            };

            deleteReq.onerror = (event) => {
              clearTimeout(timeoutId);
              logger.debug(`LOGOUT: Error deleting database ${dbName}:`, event);
              resolve(false); // Don't throw, just mark as failed
            };

            deleteReq.onblocked = () => {
              clearTimeout(timeoutId);
              logger.warn(`LOGOUT: Database deletion blocked for: ${dbName}`);
              // Note: LoginContext.js also sets matrixForceClearStores as backup before page reload
              resolve(false); // Database is in use, can't delete
            };
          } catch (e) {
            clearTimeout(timeoutId);
            logger.debug(`LOGOUT: Exception deleting database ${dbName}:`, e);
            resolve(false);
          }
        });
      };

      // Known database names to delete (fallback list)
      const knownDatabases = [
        'matrix-js-sdk::matrix-sdk-crypto', // Rust crypto store
        'matrix-js-sdk:crypto',
        'matrix-js-sdk:riot-web-sync',
        'ti-m-matrix',
        'matrix-store',
        'ti-m-media-cache',
      ];

      // Try to get list of databases if API is available
      let databasesToDelete = [];

      if (window.indexedDB.databases && typeof window.indexedDB.databases === 'function') {
        try {
          const databases = await window.indexedDB.databases();

          // Filter for Matrix/media-related databases
          const matrixDatabases = databases.filter(db => {
            if (!db.name) return false;

            // Delete anything Matrix/media-related
            return (
              db.name.includes('matrix') ||
              db.name.includes('crypto') ||
              db.name === 'matrix-js-sdk::matrix-sdk-crypto' ||
              db.name.includes('ti-m-matrix') ||
              db.name.includes('matrix-js-sdk') ||
              db.name.includes('matrix-store') ||
              db.name === 'ti-m-media-cache'
            );
          });

          databasesToDelete = matrixDatabases.map(db => db.name);
          logger.debug(`LOGOUT: Found ${databasesToDelete.length} Matrix databases to delete`);
        } catch (e) {
          logger.debug('LOGOUT: Could not enumerate databases, using fallback list:', e);
          databasesToDelete = knownDatabases;
        }
      } else {
        logger.debug('LOGOUT: databases() API not supported, using fallback list');
        databasesToDelete = knownDatabases;
      }

      // Add known databases to ensure they're included
      const allDatabases = [...new Set([...databasesToDelete, ...knownDatabases])];

      // Delete all databases with timeout
      const deletionPromises = allDatabases.map(dbName =>
        deleteDBWithTimeout(dbName, 3000), // 3 second timeout per database
      );

      // Wait for all deletions but with overall timeout
      let overallTimeoutId;
      let deletionResults = [];
      try {
        const overallTimeout = new Promise(resolve => {
          overallTimeoutId = setTimeout(() => {
            logger.warn('LOGOUT: Overall database cleanup timeout reached');
            indexedDBDeletionFailed = true; // Timeout = failure
            resolve([]); // Return empty array to signal timeout
          }, 10000); // 10 second overall timeout
        });

        deletionResults = await Promise.race([
          Promise.all(deletionPromises),
          overallTimeout,
        ]);

        // Check if any individual deletion failed
        if (deletionResults.some(result => result === false)) {
          indexedDBDeletionFailed = true;
          logger.warn('LOGOUT: One or more IndexedDB deletions failed');
        }
      } finally {
        clearTimeout(overallTimeoutId);
      }

      logger.info(`LOGOUT: IndexedDB cleanup completed (deletionFailed: ${indexedDBDeletionFailed})`);
    } catch (error) {
      logger.error('LOGOUT: Failed to clear Matrix databases:', error);
      indexedDBDeletionFailed = true;
      // Continue with logout even if database cleanup fails
    }

    // 7. Clear window state variables and session storage flags
    try {
      window.__recoveryCheckDone = false;
      // Clear the skip flag from sessionStorage to allow recovery modal in next session
      sessionStorage.removeItem(RECOVERY_SKIPPED_KEY);
      logger.debug('LOGOUT: Window state variables and recovery skip flag cleared');
    } catch (error) {
      logger.error('LOGOUT: Failed to clear window state:', error);
    }

    // 8. Final verification that recovery state is cleared
    try {
      const finalRecoveryCheck = sessionStorage.getItem('recovery:v1');
      if (finalRecoveryCheck) {
        logger.error('LOGOUT: WARNING - recovery:v1 still present at end of logout!');
        sessionStorage.removeItem('recovery:v1');
        logger.debug('LOGOUT: Force removed recovery:v1 one final time');
      } else {
        logger.debug('LOGOUT: Verified recovery:v1 is cleared');
      }
    } catch (error) {
      logger.error('LOGOUT: Failed final recovery state check:', error);
    }

    logger.info(`LOGOUT: Logout completed successfully (indexedDBDeletionFailed: ${indexedDBDeletionFailed})`);
    return { success: true, indexedDBDeletionFailed };
  }

  /**
   * Get available homeservers (for future use)
   */
  getKnownHomeservers() {
    return [
      {
        name: 'Netsfere Dev',
        url: DEFAULT_HOMESERVER,
        description: 'Development Matrix server',
      },
      {
        name: 'Matrix.org',
        url: 'https://matrix.org',
        description: 'Official Matrix homeserver',
      },
    ];
  }

  /**
   * Discover homeserver information
   */
  async discoverHomeserver(domain) {
    try {
      const wellKnownUrl = `https://${domain}/.well-known/matrix/client`;
      const response = await timFetch(wellKnownUrl);

      if (response.ok) {
        const wellKnown = await response.json();
        return {
          homeserver: wellKnown['m.homeserver']?.base_url,
          identityServer: wellKnown['m.identity_server']?.base_url,
        };
      }

      return null;
    } catch (error) {
      logger.error('Homeserver discovery failed:', error);
      return null;
    }
  }

  /**
   * Set recovery actions dispatcher (to be called from app initialization)
   * @param {Object} actions - Recovery action creators
   */
  setRecoveryActions(actions) {
    this.recoveryActions = actions;
  }

  /**
   * Detect if recovery key is required for the authenticated client
   * @param {MatrixClient} client - Authenticated Matrix client
   * @returns {Promise<void>}
   */
  async detectRecoveryRequirement(client) {
    logger.debug('RECOVERY: [AuthService] detectRecoveryRequirement called');
    const myNonce = this._loginNonce;
    // Coalesce concurrent calls with module-scoped promise — but ONLY if
    // they belong to the same session. Otherwise a leftover promise from a
    // just-invalidated session would be returned to the new client and its
    // timeout payload would trip ssss_detection_failed on the new session.
    if (recoveryDetectionInFlight && recoveryDetectionInFlight._loginNonce === myNonce) {
      logger.debug('RECOVERY: [AuthService] Recovery detection already in progress, waiting...');
      return recoveryDetectionInFlight;
    }

    const myPromise = this._performRecoveryDetection(client);
    myPromise._loginNonce = myNonce;
    recoveryDetectionInFlight = myPromise;

    try {
      await myPromise;
    } finally {
      // Only clear the slot if it still holds OUR promise. A concurrent
      // logout() or newer detection may have already replaced it — nulling
      // it here would strand the newer P2.
      if (recoveryDetectionInFlight === myPromise) {
        recoveryDetectionInFlight = null;
      }
    }
  }

  /**
   * Internal method to perform recovery detection with timeout
   * @private
   */
  async _performRecoveryDetection(client) {
    logger.debug('RECOVERY: [AuthService] Starting recovery detection');
    // Capture the nonce at entry. If `_loginNonce` advances before this
    // detection settles (e.g., the session was invalidated and the user
    // logged back in), we must NOT dispatch this result into the new
    // session's recoveryActions — it would stomp on the new session's
    // reducer with stale or DETECTION_TIMEOUT state. This complements
    // the coalescing guard in detectRecoveryRequirement, which protects
    // the INBOUND side; this check protects the OUTBOUND (dispatch) side.
    const myNonce = this._loginNonce;
    const timeout = 30000; // 30 seconds timeout - generous for slow networks
    let timeoutId;
    let result;
    try {
      const timeoutPromise = new Promise((resolve) => {
        timeoutId = setTimeout(() => {
          logger.warn('RECOVERY: [AuthService] Recovery detection timeout - returning error state');
          resolve({
            hasBackup: null,     // null = unknown (not false)
            backupInfo: null,
            hasSSSS: null,       // null = unknown (not false)
            needsRecovery: false,
            error: {
              code: 'DETECTION_TIMEOUT',
              message: 'Could not determine backup status - server did not respond in time',
            },
          });
        }, timeout);
      });

      const detectionPromise = this._detectRecoveryInternal(client);

      result = await Promise.race([detectionPromise, timeoutPromise]);
    } finally {
      clearTimeout(timeoutId);
    }

    // Dispatch only the public fields to the context (no internals)
    // Never suggest backup setup when we have an error (server state unknown)
    // Preserve null values from detection (null = unknown state, false = known state)
    const dispatchData = {
      hasBackup: result.error ? null : (result.hasBackup === null ? null : Boolean(result.hasBackup)),
      backupInfo: result.backupInfo || null,
      hasSSSS: result.error ? null : (result.hasSSSS === null ? null : Boolean(result.hasSSSS)),
      needsRecovery: result.error ? false : !!result.needsRecovery,
      needsBackupSetup: result.error ? false : !!result.needsBackupSetup,
      error: result.error || null,
    };

    if (this._loginNonce !== myNonce) {
      logger.debug(
        'RECOVERY: [AuthService] Discarding detection result from prior session ' +
        `(nonce ${myNonce} → ${this._loginNonce})`,
      );
      return;
    }

    if (this.recoveryActions) {
      logger.debug('RECOVERY: [AuthService] Dispatching init with:', dispatchData);
      this.recoveryActions.init(dispatchData);
    } else {
      logger.warn('RECOVERY: [AuthService] Recovery actions not configured - state update skipped');
    }
  }

  /**
   * Internal implementation of recovery detection logic
   * @private
   */
  async _detectRecoveryInternal(client) {
    const result = {
      hasBackup: false,
      backupInfo: null,
      hasSSSS: false,
      needsRecovery: false,
    };

    try {
      // Ensure crypto is initialized
      if (!client.getCrypto()) {
        logger.debug('Crypto not initialized, attempting to initialize...');

        // Only initialize crypto, don't start the client sync here
        // The sync should be started by MatrixClientProvider to avoid conflicts
        try {
          await client.initCrypto();
          logger.debug('Crypto initialized for recovery status check');

          // Wait a moment for crypto to be ready
          await new Promise(resolve => setTimeout(resolve, 500));
        } catch (cryptoError) {
          logger.warn('Could not initialize crypto for recovery check:', cryptoError.message);
          // Continue anyway - recovery status can still be partially determined
        }
      }

      // Run parallel checks where safe
      const [backupStatus, ssssStatus, cryptoStatus] = await Promise.all([
        this._checkBackupStatus(client),
        this._checkSSSSStatus(client),
        this._checkCryptoStatus(client),
      ]);

      logger.debug('[matrixAuthService] backupStatus:', JSON.stringify(backupStatus));
      logger.debug('[matrixAuthService] ssssStatus:', JSON.stringify(ssssStatus));
      logger.debug('[matrixAuthService] cryptoStatus:', JSON.stringify(cryptoStatus));

      result.hasBackup = backupStatus.hasBackup;
      result.backupInfo = backupStatus.backupInfo;
      result.hasSSSS = ssssStatus.hasSSSS;

      // Determine if recovery is needed
      const { xsignStatus, deviceTrust } = cryptoStatus;
      const backupTrusted = !!backupStatus.trusted;
      const hasUntrustedBackup = result.hasBackup && !backupTrusted;

      // needsRecovery: true if there's a backup that needs to be recovered
      result.needsRecovery = !!result.hasBackup &&
                             (hasUntrustedBackup || !xsignStatus?.ready || deviceTrust?.isVerified() !== true);

      // needsBackupSetup: true if there's no backup on server - user needs to create one
      // Note: We only check hasBackup, not hasSSSS, because the Rust crypto SDK automatically
      // creates SSSS during initialization. This means hasSSSS will always be true after
      // the SDK starts, even when the user has never set up backup before.
      result.needsBackupSetup = !result.hasBackup;

      logger.debug('[matrixAuthService] needsBackupSetup calculation:', {
        hasBackup: result.hasBackup,
        hasSSSS: result.hasSSSS,
        needsBackupSetup: result.needsBackupSetup,
        needsRecovery: result.needsRecovery,
      });

      logger.debug('RECOVERY: [AuthService] Detection complete:', {
        hasBackup: result.hasBackup,
        hasSSSS: result.hasSSSS,
        needsRecovery: result.needsRecovery,
        backupTrusted,
        hasUntrustedBackup,
        xsignReady: xsignStatus?.ready,
        deviceVerified: deviceTrust?.isVerified(),
      });

    } catch (error) {
      logger.error('Recovery detection error - using conservative defaults:', error);
      // Return conservative defaults on error with error info
      result.error = error.message || 'Recovery detection failed';
    }

    return result;
  }

  /**
   * Check backup status
   * @private
   */
  async _checkBackupStatus(client) {
    try {
      // Prefer Rust crypto APIs when available as they are more reliable
      const crypto = typeof client.getCrypto === 'function' ? client.getCrypto() : null;

      if (crypto && typeof crypto.getKeyBackupInfo === 'function') {
        // getKeyBackupInfo returns null if no backup exists
        const info = await crypto.getKeyBackupInfo();
        if (!info) {
          return { hasBackup: false, backupInfo: null };
        }

        // Determine trust via crypto.isKeyBackupTrusted when available
        let isTrusted = false;
        if (typeof crypto.isKeyBackupTrusted === 'function') {
          try {
            const trust = await crypto.isKeyBackupTrusted(info);
            isTrusted = !!(trust && trust.trusted === true);
          } catch {
            // If trust check fails, treat as untrusted (do not throw)
            isTrusted = false;
          }
        }

        // Sanitize to safe fields only
        const sanitizedInfo = {
          algorithm: info.algorithm || 'unknown',
          version: info.version || 'unknown',
          etag: info.etag || null,
        };

        return { hasBackup: true, backupInfo: sanitizedInfo, trusted: isTrusted };
      }

      // Fallback to client-level APIs
      const [isBackupEnabled, backupInfo] = await Promise.all([
        typeof client.isKeyBackupEnabled === 'function' ? client.isKeyBackupEnabled() : Promise.resolve(false),
        typeof client.getKeyBackupVersion === 'function' ? client.getKeyBackupVersion() : Promise.resolve(null),
      ]);

      const hasBackupOnServer = !!(backupInfo && backupInfo.version);
      if (!hasBackupOnServer) {
        return { hasBackup: false, backupInfo: null };
      }

      // Sanitize backup info - only include safe fields
      const sanitizedInfo = {
        algorithm: backupInfo.algorithm || 'unknown',
        version: backupInfo.version || 'unknown',
        etag: backupInfo.etag || null,
      };

      // In the legacy path, treat "enabled" as proxy for trusted
      return { hasBackup: true, backupInfo: sanitizedInfo, trusted: !!isBackupEnabled };
    } catch (error) {
      logger.error('Error checking backup status:', error);
      return { hasBackup: false, backupInfo: null };
    }
  }

  /**
   * Check SSSS (Secure Secret Storage and Sharing) status
   * Note: This is now only used for logging/debugging. The needsBackupSetup
   * decision is based solely on hasBackup, not hasSSSS.
   * @private
   */
  async _checkSSSSStatus(client) {
    try {
      // CRITICAL: getAccountDataFromServer() returns LOCAL CACHE after initial sync!
      // We must make a direct HTTP call to truly query the server.
      const userId = client.getUserId();
      const baseUrl = client.getHomeserverUrl();
      const accessToken = client.getAccessToken();

      const url = `${baseUrl}/_matrix/client/v3/user/${encodeURIComponent(userId)}/account_data/m.secret_storage.default_key`;

      const response = await timFetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (response.status === 404) {
        // Server confirms no SSSS exists
        return { hasSSSS: false };
      }

      if (!response.ok) {
        // Non-404 error response - server state unknown, return null to prevent auto-bootstrap
        logger.warn('SSSS status check returned error:', response.status);
        return { hasSSSS: null };
      }

      return { hasSSSS: true };
    } catch (error) {
      // Network error - server state unknown, return null to prevent destructive auto-bootstrap
      logger.error('Error checking SSSS status:', error);
      return { hasSSSS: null };
    }
  }

  /**
   * Check crypto status (cross-signing and device trust)
   * @private
   */
  async _checkCryptoStatus(client) {
    const crypto = client.getCrypto();

    if (!crypto) {
      logger.warn('Crypto not available');
      return { xsignStatus: null, deviceTrust: null };
    }

    try {
      const [xsignStatus, deviceTrust] = await Promise.all([
        crypto.getCrossSigningStatus().catch(err => {
          logger.error('Error getting cross-signing status:', err);
          return null;
        }),
        crypto.getDeviceVerificationStatus(
          client.getUserId(),
          client.getDeviceId(),
        ).catch(err => {
          logger.error('Error getting device verification status:', err);
          return null;
        }),
      ]);

      return { xsignStatus, deviceTrust };
    } catch (error) {
      logger.error('Error checking crypto status:', error);
      return { xsignStatus: null, deviceTrust: null };
    }
  }

  /**
   * Finalize login process with recovery detection
   * @param {Object} matrixCredentials - Successful login credentials
   */
  async finalizeLogin(matrixCredentials) {
    try {
      // Store credentials first
      this.storeCredentials(matrixCredentials);

      // NED-3466 follow-up: a fresh session has started. Bumping the nonce
      // means any lingering detection promise from the prior session fails
      // the identity check in detectRecoveryRequirement and is replaced.
      this._loginNonce++;
      // Defensive: clear a stuck single-flight flag that could have been
      // left true by an abnormal teardown in the prior session.
      this._sessionInvalidating = false;

      // Mark recovery check as done for this session
      window.__recoveryCheckDone = true;

      // Attempt to rehydrate recovery key for session
      try {
        const sessionId = `${matrixCredentials.userId}:${matrixCredentials.deviceId}`;
        const { default: recoveryKeyValidator } = await import('./RecoveryKeyValidator');

        const rehydrated = await recoveryKeyValidator.rehydrate(sessionId);
        if (rehydrated) {
          logger.debug('Recovery key rehydrated from storage');
        }
      } catch (error) {
        logger.debug('Recovery key rehydration skipped:', error.message);
        // Non-fatal - continue without rehydration
      }

      // Return success immediately - recovery detection will be done by the main app
      return {
        success: true,
        client: null, // Client will be created by MatrixService
      };

    } catch (error) {
      logger.error('Error finalizing login:', error);
      // Don't throw - just log and return success since credentials are stored
      return {
        success: true,
        client: null,
      };
    }
  }

  /**
   * Handle SSO callback with recovery detection
   * @param {string} accessToken - SSO access token
   * @param {string} deviceId - Device ID from SSO
   * @param {Object} options - Additional SSO options
   */
  async handleSSOCallback(accessToken, deviceId, options = {}) {
    try {
      // Prevent duplicate detection
      if (window.__recoveryCheckDone) {
        logger.debug('Recovery check already performed this session');
        return { success: true };
      }

      const matrixCredentials = {
        homeserverUrl: options.homeserver || this.defaultHomeserver,
        identityServerUrl: options.identityServer || this.defaultIdentityServer,
        userId: options.userId,
        deviceId: deviceId,
        accessToken: accessToken,
        refreshToken: options.refreshToken,
        expiresInMs: options.expiresInMs,
        wellKnown: options.wellKnown,
      };

      // Finalize login with recovery detection
      return await this.finalizeLogin(matrixCredentials);

    } catch (error) {
      logger.error('SSO callback handling failed:', error);
      throw error;
    }
  }
}

// Create singleton instance
const matrixAuthService = new MatrixAuthService();

// Test-only hooks for the module-scoped recoveryDetectionInFlight binding.
// NODE_ENV guard lets webpack tree-shake the object out of production bundles
// (mirrors the dev-only affordance pattern in matrixService.js bottom).
// Jest sets NODE_ENV=test, so tests can import __testing__ normally.
export const __testing__ = process.env.NODE_ENV !== 'production' ? {
  resetRecoveryDetectionInFlight: () => { recoveryDetectionInFlight = null; },
  getRecoveryDetectionInFlight: () => recoveryDetectionInFlight,
  setRecoveryDetectionInFlight: (p) => { recoveryDetectionInFlight = p; },
} : undefined;

export { isEmailAddress, parseMatrixId };
export default matrixAuthService;
